import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { ChordBuilder } from "@/components/mt/ChordBuilder";
import { ChordTree } from "@/components/mt/ChordTree";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory26() {
  const { lesson, course, accent } = findLesson("music-theory", "26");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT26-Chord-Trees-Chromatic-Animated.mp4"
        caption="Chord trees — any single note grows 6 triads (3 major + 3 minor)"
        hint="≈ 34 sec · watch first"
      />
      <Chapter number="26" eyebrow="Music Theory · Chapter Twenty-Six" title="Chord trees · Part 1: the chromatic field around a single note">
        <p>
          Most chord books teach chords as a list — major triads, minor triads, sevenths,
          extensions — like flashcards. The trouble with a list is it doesn't tell you
          when to use one. The moment you have a melody in front of you and a single,
          stubborn note ringing under your fingers, the question is no longer "what is a
          C major chord?" It's <em>"how many chords contain this C?"</em>
        </p>
        <p>
          That is the question this chapter is built around. The answer is a small,
          surprising menu of options — six triads if we limit ourselves to plain major and
          minor — and once you can see that menu, you have a tool that bridges melody and
          harmony from either direction. Got a melody and need chords for it? The chord
          tree gives you choices. Got chords and need a melody? The same tree, walked the
          other way.
        </p>
        <MTQuote>
          That important point would probably be at beat one of the bar. It would last
          longer. It would be the most important word of the song. It could probably be an
          adjective like bad or sad, or it could be a proper noun like Jason or Jude or
          whoever it may be. So at those important words we harmonize those respective
          landing notes.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="The setup" title="One note, six branches">
        <Prose>
          <p>
            Let's pick the note <strong>C</strong> and ask the central question: how many
            triads — major or minor only — contain this C? A triad has three notes: a
            root, a third (major or minor), and a perfect fifth. So the C we're holding
            could be sitting in any one of those three slots. That gives us a clean,
            methodical search.
          </p>
          <p>
            The trick is to <em>reverse the interval question</em>. We're used to asking
            "what is a perfect fifth above C?" and answering G. But to find the chord tree,
            we ask the opposite: "C is the perfect fifth of what?" That answer is F. Run
            the same flip for the third — major and minor — and the menu fills out fast.
          </p>
        </Prose>

        <TheoryBox label="The reverse-interval flip">
          To build a chord tree on a note, ask three questions in this order:
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-[13px]">
            <li><strong>This note is the root of which major / minor triads?</strong></li>
            <li><strong>This note is the perfect fifth of which major / minor triads?</strong></li>
            <li><strong>This note is the major / minor third of which triads?</strong></li>
          </ol>
          <div className="mt-2 text-[12px] text-nsm-dim">
            Six answers. Six branches. That's your tree.
          </div>
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Walking the tree of C" title="The six chords that contain C">
        <Steps>
          <Step><strong>C as the root.</strong> The two namesake chords — <span className="font-mono text-amber-300">C major</span> (C&nbsp;E&nbsp;G) and <span className="font-mono text-amber-300">C minor</span> (C&nbsp;E♭&nbsp;G).</Step>
          <Step><strong>C as the perfect fifth.</strong> The fifth above F is C — so C lives inside <span className="font-mono text-amber-300">F major</span> (F&nbsp;A&nbsp;C) and <span className="font-mono text-amber-300">F minor</span> (F&nbsp;A♭&nbsp;C).</Step>
          <Step><strong>C as the major third.</strong> A major third above A♭ is C — so <span className="font-mono text-amber-300">A♭ major</span> (A♭&nbsp;C&nbsp;E♭) carries it.</Step>
          <Step><strong>C as the minor third.</strong> A minor third above A is C — so <span className="font-mono text-amber-300">A minor</span> (A&nbsp;C&nbsp;E) does too.</Step>
        </Steps>

        <Prose>
          <p>
            Six triads, and every single one of them holds the note C somewhere inside.
            Three are major, three are minor. The interval chart and the circle of fifths
            are both useful at this point — the circle hands you the fifths instantly, and
            an interval table gives you the major and minor third relationships in either
            direction.
          </p>
          <p>
            Here is the chromatic chord tree of C, laid out as buttons you can hear. Click
            each one and listen to how the same note C sits in six totally different
            harmonic beds:
          </p>
        </Prose>

        <ChordBuilder root="C" families={["triad"]} />

        <Prose>
          <p>
            Watch the interval chips for each chord — every one of them includes a
            <span className="font-mono"> C</span> somewhere. That is the entire premise of
            the tree.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Beyond the basics" title="If you want to go further: sevenths, sus, diminished, augmented">
        <Prose>
          <p>
            We deliberately limited the search to plain major and minor triads — the
            workhorses of pop, rock, country, film and most folk music worldwide. But the
            tree has more to offer if you keep asking the reverse-interval question with a
            wider net.
          </p>
          <ul className="space-y-1 text-[14px]">
            <li><strong>C as the major 7th</strong> — that's <span className="font-mono">D♭ major 7th</span> (and you can flip the third to make <span className="font-mono">D♭ minor major 7th</span>).</li>
            <li><strong>C as the minor 7th</strong> — that's <span className="font-mono">D minor 7th</span> and <span className="font-mono">D dominant 7th</span>.</li>
            <li><strong>C as an augmented 5th</strong> — sneaks in <span className="font-mono">E augmented</span>, <span className="font-mono">A♭ augmented</span>, <span className="font-mono">C augmented</span>.</li>
            <li><strong>C as a diminished 5th</strong> — adds <span className="font-mono">F♯ diminished</span>.</li>
            <li><strong>C as a suspended note</strong> — gives you <span className="font-mono">G sus4</span> (where C is the suspended 4th).</li>
          </ul>
          <p>
            For pop, rock and country writing, six is plenty. For jazz, film scoring and
            reharmonisation, the tree expands as much as your harmonic vocabulary lets it.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="A second tree" title="The chord tree of A">
        <Prose>
          <p>
            One example is never enough — let's run the same question on the note
            <strong> A</strong>. Same three questions, same six branches:
          </p>
        </Prose>

        <Steps>
          <Step><strong>A as root.</strong> <span className="font-mono text-amber-300">A major</span> and <span className="font-mono text-amber-300">A minor</span>.</Step>
          <Step><strong>A as the perfect fifth.</strong> <span className="font-mono text-amber-300">D major</span> and <span className="font-mono text-amber-300">D minor</span> — both have A on top.</Step>
          <Step><strong>A as the major third.</strong> <span className="font-mono text-amber-300">F major</span> (F&nbsp;A&nbsp;C).</Step>
          <Step><strong>A as the minor third.</strong> <span className="font-mono text-amber-300">F♯ minor</span> (F♯&nbsp;A&nbsp;C♯).</Step>
        </Steps>

        <Prose>
          <p>
            Notice what's now under your hands: A major, A minor, D major, D minor, F major,
            F♯ minor. If you anchor an entire phrase on the single note <strong>A</strong>
            and float between those six chords, you can produce music that sounds
            unexpectedly cinematic — non-diatonic but never random — because every chord
            holds the same A pinning the harmony to one place in your ear.
          </p>
        </Prose>

        <ChordBuilder root="A" families={["triad"]} />
      </Chapter>

      <Chapter eyebrow="The Adele example" title="Anchoring on one note · the James Bond colour">
        <Prose>
          <p>
            Listen to <em>Skyfall</em> by Adele. The lyric "this is the end" sits on a held
            <strong> C</strong>, and underneath it the harmony shifts:
            <span className="font-mono"> C minor → A♭ major → F major</span>.
            That's a tour through the chord tree of C — root, then C-as-major-third
            (A♭ major), then C-as-fifth (F major). Three chords, all containing C, all
            very different in colour.
          </p>
          <p>
            That move is sometimes called the <em>James Bond progression</em> — i&nbsp;→&nbsp;♭VI&nbsp;→&nbsp;IV.
            Very Dorian. Very brooding. And it works precisely because the melody refuses
            to leave the C while the harmony cycles through the tree underneath.
          </p>
        </Prose>

        <TheoryBox label="Why the chord tree matters">
          The chord tree is what you reach for when one chord per landing note feels
          predictable. Bollywood pop, beginner pianists, school choirs — they often pick
          the most obvious chord (the namesake root). The tree shows you five other
          options for the same note, instantly opening up doors to film-score writing,
          jazz reharmonisation, and modal pop.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="What's a landing note?" title="The melody side of the equation">
        <Prose>
          <p>
            The chord tree only earns its keep when you know where to plant it. The right
            place is on what we call a <strong>landing note</strong> — a melodic note that
            is, in some way, more important than the notes around it. Common signs that a
            note is a landing note:
          </p>
          <ul className="space-y-1 text-[14px]">
            <li>It sits on <strong>beat one</strong> of the bar (sometimes beat three).</li>
            <li>It is held <strong>longer</strong> than the notes on either side.</li>
            <li>It carries an <strong>important word</strong> — a proper noun, an adjective, a verb of feeling.</li>
            <li>It is the <strong>top</strong> or the <strong>bottom</strong> of a melodic phrase — the climax or the resolution.</li>
          </ul>
          <p>
            Identify those landing notes, plant a chord tree on each one, and you have a
            menu of harmonic options for every important moment of the melody. That is
            exactly the workflow we'll apply to a real song in the next chapter.
          </p>
        </Prose>

        <ChordTree tonic="C" mode="major" />

        <Prose>
          <p>
            (The chord tree above is the <em>diatonic</em> version we'll spend the next
            chapter on. The chromatic version we just built reaches outside the key — three
            of its six chords do. C major, F major and A minor are all diatonic to C major;
            C minor, F minor and A♭ major are not. That colour-outside-the-lines quality is
            what gives chromatic chord trees their cinematic flavour.)
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Homework" title="Build the tree on every note">
        <Prose>
          <p>
            There are twelve notes in music — so there are twelve chromatic chord trees.
            Take a notebook and a pen. Do not type this on a computer. Write each note as
            a heading and list its six chords underneath. The act of writing is what
            burns the relationships into your hand and your ear. By the time you finish
            all twelve, the reverse-interval flip will be automatic, and reaching for
            non-obvious chords under a stuck melody note will feel as natural as breathing.
          </p>
        </Prose>

        <Glossary>
          <Term name="Chord tree" def="The set of chords that contain a given note. Six triads if we limit to major + minor." />
          <Term name="Landing note" def="An important melodic note — long, on a strong beat, or carrying a key word. The natural place to plant a chord tree." />
          <Term name="Reverse-interval flip" def="Asking 'this note is the [interval] of which chord?' instead of the other way around." />
          <Term name="Chromatic" def="Outside the diatonic seven. The chord tree of C reaches A♭ major and F minor — both chromatic to C major." />
        </Glossary>
        <NoteImage
          src="/notes/music-theory/finals/26a-chord-trees-of-C-and-A.png"
          caption="Jason's note: the 'chord tree' — every triad reachable from a single root via P5 (up & down) and M3 / m3. From C: G (up P5), F (down P5), E or Em (M3 / m3 up), A♭ or Am (M3 / m3 down). Six neighbours = the main chromatic chord network. The same template applied to A gives a totally different tree."
        />
        <NoteImage
          src="/notes/music-theory/finals/26b-tree-formula-major-minor-on-each-arm.png"
          caption="Each arm of the tree forks into a major and minor variant: from any root you can reach 6 chords (root major/minor, P5 major/minor, M3 major/minor + m3 major/minor) — the foundation of chromatic chord substitution."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-27"
        label="Chapter 27 · Chord Trees - Diatonic (Part 2)"
        why="Now we narrow the tree to a single key — only the seven chords inside the scale — and you'll see why this version is the one that actually answers 'what chord goes here?' for almost any pop, rock or film song."
      />
    </LessonShell>
  );
}
